import { ContentObserver } from '@angular/cdk/observers';
import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  NgZone,
  Output,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MaxLinesHelper } from './max-lines.helper';
import { FontColor, ReadMore, ReadMoreColor } from '../text/text.types';

/**
 * This directive is to be used with the tailwind text.
 * This is copying most of the logic from text.component.ts. It leaves out a few things that are not needed for tailwind text.
 * This directive and truncation logic is to be reworked.
 */
@Directive({
  selector: '[flMaxLines]',
  standalone: true,
})
export class MaxLinesDirective implements OnChanges, AfterViewInit, OnDestroy {
  @Input() flMaxLines?: number;
  @Input() flReadMore = false;
  @Input() flReadMoreType: ReadMore = ReadMore.LINK;

  @Input()
  @HostBinding('style.max-height')
  maxHeight?: string;

  @Input() flReadMoreColor?: ReadMoreColor;
  /**
   * Whether to always show the "read more" button when `maxLines` is set even if the text fits within those lines.
   * Used when we have some custom truncation logic with the `expand` output.
   */
  @Input() flAlwaysShowReadMore = false;

  @Output()
  expand = new EventEmitter<boolean>();

  @HostBinding('style.overflow')
  overflow?: string;

  private container: HTMLElement;
  private originalChildNodes: Node[] = [];
  private originalTextMap = new Map<Text, string>();
  private isInTransition: boolean;
  private maxLinesHelper: MaxLinesHelper;
  private subscriptions = new Subscription();
  private isExpanded = false;
  private animationRequestId: number | undefined = undefined;
  private windowSize: { width?: number; height?: number } = {};
  private cssLineClampApplied = false;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly elementRef = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly renderer = inject(Renderer2);
  private readonly obs = inject(ContentObserver);
  private readonly timeUtils = inject(TimeUtils);

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId) || !this.container) {
      return;
    }

    const relevant =
      'flMaxLines' in changes ||
      'flReadMore' in changes ||
      'flAlwaysShowReadMore' in changes;
    if (!relevant) {
      return;
    }

    if (this.flMaxLines === undefined) {
      this.removeCssLineClamp();
      this.restoreContainer();
      this.maxLinesHelper?.removeReadMoreButton();
      return;
    }

    if (this.isCssFastPathEligible()) {
      // Switching into fast path: tear down any active JS truncation first.
      this.destroyListeners();
      this.maxLinesHelper?.removeReadMoreButton();
      this.restoreContainer();
      this.applyCssLineClamp();
    } else {
      this.removeCssLineClamp();
      this.truncate();
    }
  }

  ngAfterViewInit(): void {
    if (!this.flMaxLines || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.container = this.elementRef.nativeElement;

    if (this.isCssFastPathEligible()) {
      this.applyCssLineClamp();
      return;
    }

    // Set display to block for proper line-height calculation, similar to fl-text behavior
    this.setDisplayBlockForSpan();

    this.subscriptions.add(
      this.ngZone.onStable
        .asObservable()
        .pipe(
          filter(() => !!this.container.scrollHeight),
          take(1),
        )
        .subscribe(() => {
          this.truncate();
        }),
    );
  }

  private isCssFastPathEligible(): boolean {
    return (
      isDefined(this.flMaxLines) &&
      !this.flReadMore &&
      !this.flAlwaysShowReadMore
    );
  }

  private applyCssLineClamp(): void {
    if (!this.container || this.flMaxLines === undefined) {
      return;
    }
    const el = this.container;
    const lines = String(this.flMaxLines);
    this.renderer.setStyle(el, 'display', '-webkit-box');
    this.renderer.setStyle(el, '-webkit-box-orient', 'vertical');
    this.renderer.setStyle(el, '-webkit-line-clamp', lines);
    this.renderer.setStyle(el, 'line-clamp', lines);
    this.renderer.setStyle(el, 'overflow', 'hidden');
    this.cssLineClampApplied = true;
  }

  private removeCssLineClamp(): void {
    if (!this.cssLineClampApplied || !this.container) {
      return;
    }
    const el = this.container;
    this.renderer.removeStyle(el, 'display');
    this.renderer.removeStyle(el, '-webkit-box-orient');
    this.renderer.removeStyle(el, '-webkit-line-clamp');
    this.renderer.removeStyle(el, 'line-clamp');
    this.renderer.removeStyle(el, 'overflow');
    this.cssLineClampApplied = false;
  }

  truncate(): void {
    if (!isDefined(this.flMaxLines)) {
      return;
    }

    this.container = this.elementRef.nativeElement;
    if (!this.container) {
      return;
    }

    // tldr truncate logic: we cache the nodes by creating a map of nodes and their original text content.
    // key: node, value: original text content
    // goal is to restore the original text content after truncation when 'read more' is invoked.
    // just fetch the original text content from the map and set it to the node's text content.
    this.originalChildNodes = Array.from(this.container.childNodes);
    this.originalTextMap.clear();
    const walker = document.createTreeWalker(
      this.container,
      NodeFilter.SHOW_TEXT,
    );
    while (walker.nextNode()) {
      const txt = walker.currentNode as Text;
      this.originalTextMap.set(txt, txt.textContent ?? '');
    }

    this.isExpanded = false;
    this.destroyListeners();

    // Ensure display is set to block for proper line-height calculation on span elements
    this.setDisplayBlockForSpan();

    this.maxLinesHelper = new MaxLinesHelper(this.container, isTruncated => {
      if (!isTruncated) {
        this.isInTransition = true;
        this.isExpanded = true;
        this.renderer.setStyle(
          this.elementRef.nativeElement,
          'max-height',
          'none',
        );

        this.restoreContainer();
      }

      this.expand.emit(!isTruncated);

      // This indicates that the transition is completed.
      this.animationRequestId = requestAnimationFrame(() => {
        this.isInTransition = false;
      });
    });

    this.maxLinesHelper.truncate(
      this.flMaxLines,
      this.flReadMore ? this.flReadMoreType : ReadMore.NONE,
      FontColor.FOREGROUND,
      this.flReadMoreColor,
      this.flAlwaysShowReadMore,
    );

    // listen for changes of content
    this.subscriptions.add(
      this.obs
        .observe(this.container)
        .pipe(filter(() => !!this.container.scrollHeight))
        .subscribe(() => {
          // do nothing if the mutation is the result of the element expanding
          // or if the text has already been expanded by the user
          if (!this.isInTransition && !this.isExpanded) {
            // Remove read more button before re-truncation
            this.maxLinesHelper.removeReadMoreButton();
            this.truncate();
          }
        }),
    );

    // listen for resize events
    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(this.timeUtils.rxDebounceTime(100))
        .subscribe(() => {
          // make sure the element is displayed
          if (
            this.container &&
            (this.container.offsetWidth ||
              this.container.offsetHeight ||
              this.container.getClientRects().length)
          ) {
            const newSizes = {
              width: window.innerWidth,
              height: window.innerHeight,
            };
            // only do something if the actual window size has changed
            if (
              this.windowSize.width !== newSizes.width ||
              this.windowSize.height !== newSizes.height
            ) {
              this.windowSize = newSizes;
              if (!this.isExpanded) {
                this.reTruncate();
              }
            }
          }
        }),
    );
  }

  private restoreContainer(): void {
    if (!this.container) {
      return;
    }
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    // Restore full text on every cached node from the map we created before truncation.
    this.originalTextMap.forEach((fullText, textNode) => {
      textNode.textContent = fullText;
    });
    // Re-attach the exact nodes Angular created.
    // these are the nodes that angular rendered before we mutated anything, we just reverted it's content.
    this.originalChildNodes.forEach(node => {
      this.container.appendChild(node);
    });
  }

  private reTruncate(): void {
    // Restore the original content.
    this.restoreContainer();

    this.maxLinesHelper.removeReadMoreButton();

    // Perform truncation again.
    this.truncate();
  }

  private setDisplayBlockForSpan(): void {
    if (this.container?.tagName.toLowerCase() === 'span') {
      this.renderer.setStyle(this.container, 'display', 'block');
    }
  }

  destroyListeners(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.animationRequestId) {
      cancelAnimationFrame(this.animationRequestId);
    }
  }
}
